import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router";
import {
  LayoutDashboard, Users, Briefcase, CheckSquare,
  GitCommit, Mail, ChevronDown,
  Cpu, Wrench, Package, DollarSign, Calculator,
  Layers, Globe, User,
  PanelLeft,
  UserKey,
  UserRoundCog,
} from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";
import { useMe } from "@/hooks/useMe";

interface NavLeaf {
  label: string;
  to: string;
  icon?: React.ReactNode;
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
}

interface NavSection {
  section: string;
  items: (NavLeaf | NavGroup)[];
}

const isGroup = (item: NavLeaf | NavGroup): item is NavGroup =>
  "children" in item;

const iconSize = 16;

const nav: NavSection[] = [
  {
    section: "Dashboard",
    items: [
      {
        label: "Dashboard",
        icon: <LayoutDashboard size={iconSize} />,
        children: [
          // { label: "Overview", to: "/admin/dashboard/overview" },
          { label: "Analytics", to: "/admin/analytics" },
          // { label: "Reports", to: "/admin/dashboard/reports" },
        ],
      },
    ],
  },
  {
    section: "Management",
    items: [
      { label: "Users", to: "/admin/users", icon: <Users size={iconSize} /> },
      { label: "Roles", to: "/admin/roles", icon: <UserKey size={iconSize} /> },
      { label: "Applications", to: "/admin/applications", icon: <Briefcase size={iconSize} /> },
      { label: "Responsibility Map", to: "/admin/responsibility-map", icon: <UserRoundCog size={iconSize} /> },
      { label: "Tasks", to: "/admin/tasks", icon: <CheckSquare size={iconSize} /> },
      // { label: "Approvals", to: "/admin/approvals", icon: <Shield size={iconSize} /> },
      { label: "Changelog", to: "/admin/change-log", icon: <GitCommit size={iconSize} /> },
    ],
  },
  {
    section: "Datasets",
    items: [
      {
        label: "Machine",
        icon: <Cpu size={iconSize} />,
        children: [
          { label: "Products", to: "/admin/datasets/products" },
          { label: "Machine Options EU", to: "/admin/datasets/machine-options" },
          { label: "Machine Options NA", to: "/admin/datasets/machine-options-us" },
          { label: "Profil InDie Options EU", to: "/admin/datasets/profil-indie-options" },
          { label: "Profil InDie Options NA", to: "/admin/datasets/profil-indie-options-us" },
          { label: "Autoriv Configurator Items EU", to: "/admin/datasets/autoriv-configurator-items" },
          { label: "Autoriv Configurator Items NA", to: "/admin/datasets/autoriv-configurator-items-us" },
          { label: "Sherex Options EU", to: "/admin/datasets/sherex-options" },
          { label: "Sherex Options NA", to: "/admin/datasets/sherex-options-us" },
          { label: "CAD 3D Settings", to: "/admin/datasets/cad-3d-settings" },
        ],
      },
      {
        label: "Products",
        icon: <Package size={iconSize} />,
        children: [
          { label: "PEM Fasteners", to: "/admin/datasets/fasteners" },
          { label: "Profil Fasteners", to: "/admin/datasets/profil-fasteners" },
          { label: "Autoriv Fasteners", to: "/admin/datasets/autoriv-fasteners" },
          { label: "Sherex Fasteners", to: "/admin/datasets/sherex-fasteners" },
        ],
      },
      {
        label: "Tooling",
        icon: <Wrench size={iconSize} />,
        children: [
          { label: "Fastener -> Tooling Mapping", to: "/admin/datasets/fastener-tooling-mapping" },
          { label: "Tooling Sets", to: "/admin/datasets/tooling-sets" },
        ],
      },
      {
        label: "Pricing",
        icon: <DollarSign size={iconSize} />,
        children: [
          { label: "Pemserter Price EUR", to: "/admin/datasets/pemserter-eur" },
          { label: "Pemserter Price USD", to: "/admin/datasets/pemserter-usd" },

        ],
      },
      {
        label: "Calculations",
        icon: <Calculator size={iconSize} />,
        children: [
          { label: "Calculation Parameters", to: "/admin/datasets/calculation-parameters" },
          { label: "Cost Models", to: "/admin/datasets/cost-models" },
          { label: "Cost Model Tools", to: "/admin/datasets/cost-model-tools" },
          { label: "Manual Quote Items", to: "/admin/datasets/manual-quote-items" },
        ],
      },
      {
        label: "Content",
        icon: <Globe size={iconSize} />,
        children: [
          { label: "Landing Content", to: "/admin/datasets/landing-content" },
        ],
      },
    ],
  },
  {
    section: "Integrations",
    items: [
      { label: "SMTP", to: "/admin/smtp", icon: <Mail size={iconSize} /> },
    ],
  },
  {
    section: "Settings",
    items: [
      // { label: "General", to: "/admin/settings/general", icon: <Cog size={iconSize} /> },
      // { label: "Permissions", to: "/admin/settings/permissions", icon: <Shield size={iconSize} /> },
      { label: "Profile", to: "/admin/settings/profile", icon: <User size={iconSize} /> },
      // { label: "Security", to: "/admin/settings/security", icon: <Lock size={iconSize} /> },
    ],
  },
];

/**
 * Section-level visibility gate (UI chrome only — the backend independently
 * enforces every route via `PermissionsGuard`, per §L). "Management" needs
 * `canManageUsers`; "Dashboard"/"Datasets"/"Integrations" need
 * `canManageMasterData`; "Settings" (Profile) is always visible regardless
 * of either permission.
 */
function isSectionVisible(
  section: string,
  perms: { canManageUsers: boolean; canManageMasterData: boolean },
): boolean {
  switch (section) {
    case "Management":
      return perms.canManageUsers;
    case "Dashboard":
    case "Datasets":
    case "Integrations":
      return perms.canManageMasterData;
    case "Settings":
      return true;
    default:
      return true;
  }
}

/* ── Tooltip wrapper for collapsed mode ── */
const Tooltip = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="group/tip relative flex items-center">
    {children}
    <div className="pointer-events-none absolute left-full ml-2 z-50 hidden group-hover/tip:flex items-center">
      <div className="bg-gray-900 text-white text-small-medium px-2 py-1 rounded whitespace-nowrap shadow-lg">
        {label}
      </div>
    </div>
  </div>
);

/* ── Leaf link ── */
const LeafLink = ({
  item,
  collapsed,
  depth = 0,
  onNavigate,
}: {
  item: NavLeaf;
  collapsed: boolean;
  depth?: number;
  onNavigate?: () => void;
}) => (
  <NavLink
    to={item.to}
    onClick={onNavigate}
    title={collapsed ? item.label : undefined}
    className={({ isActive }) =>
      [
        "group flex items-center gap-3 rounded-lg text-nav transition-all duration-150 select-none",
        collapsed ? "justify-center px-0 py-3 mx-auto w-9 h-9" : depth > 0 ? "px-3 py-2 ml-6" : "px-3 py-2",
        isActive
          ? "bg-penn-500 text-white shadow-sm shadow-penn-100 dark:shadow-penn-900"
          : "text-on-navy-secondary hover:bg-white/15 hover:text-on-navy dark:hover:bg-gray-800 dark:hover:text-gray-100",
      ].join(" ")
    }
  >
    {({ isActive }) => (
      <>
        {item.icon && (
          <span className={isActive ? "text-white" : "text-on-navy-tertiary group-hover:text-on-navy dark:group-hover:text-gray-300"}>
            {item.icon}
          </span>
        )}
        {!collapsed && <span className="truncate">{item.label}</span>}
      </>
    )}
  </NavLink>
);

/* ── Accordion group ── */
const GroupItem = ({
  item,
  collapsed,
  onNavigate,
  onExpandSidebar,
}: {
  item: NavGroup;
  collapsed: boolean;
  onNavigate?: () => void;
  /** Collapsed rail only: expand the sidebar so the group's children show. */
  onExpandSidebar?: () => void;
}) => {
  const location = useLocation();
  const hasActive = item.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(hasActive);

  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  if (collapsed) {
    return (
      <Tooltip label={item.label}>
        <button
          onClick={() => { setOpen(true); onExpandSidebar?.(); }}
          className="flex items-center justify-center w-9 h-9 rounded-lg text-on-navy-secondary hover:bg-white/15 hover:text-on-navy dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors mx-auto"
          aria-label={item.label}
        >
          {item.icon}
        </button>
      </Tooltip>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className={[
          "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-nav transition-all duration-150 select-none",
          hasActive
            ? "text-penn-100 bg-white/10 dark:text-penn-400 dark:bg-penn-900"
            : "text-on-navy-secondary hover:bg-white/15 hover:text-on-navy dark:hover:bg-gray-800 dark:hover:text-gray-100",
        ].join(" ")}
        aria-expanded={open}
      >
        <span className={hasActive ? "text-penn-100" : "text-on-navy-tertiary"}>
          {item.icon}
        </span>
        <span className="flex-1 text-left truncate">{item.label}</span>
        <span className={`transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}>
          <ChevronDown size={13} />
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-200 ${open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="mt-1 space-y-1 pb-1">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  "flex items-center gap-2 pl-8 pr-3 py-2 rounded-lg text-nav transition-all duration-150",
                  isActive
                    ? "bg-penn-500 text-white font-medium shadow-sm"
                    : "text-on-navy-secondary hover:bg-white/15 hover:text-on-navy dark:hover:bg-gray-800 dark:hover:text-gray-200",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? "bg-white" : "bg-gray-300 dark:bg-gray-600"}`} />
                  <span className="truncate">{child.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Section label ── */
const SectionLabel = ({ label, collapsed }: { label: string; collapsed: boolean }) =>
  collapsed ? (
    <div className="my-2 mx-auto h-px w-6 bg-gray-200 dark:bg-gray-700" />
  ) : (
    <p className="px-3 pt-4 pb-2 text-label uppercase text-on-navy-tertiary select-none">
      {label}
    </p>
  );

/* ── Logo ── */
const Logo = ({ collapsed }: { collapsed: boolean }) => (
  <div className={`flex items-center gap-3 px-4 py-4 border-b border-white/15 dark:border-gray-800 ${collapsed ? "justify-center px-2" : ""}`}>
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-penn-500 to-penn-900 flex items-center justify-center shadow-sm">
      <Layers size={16} className="text-white" />
    </div>
    {!collapsed && (
      <>
      
      <div>
        <p className="text-body-semibold text-on-navy dark:text-white">Penn Admin</p>
        {/* <p className="text-[10px] text-blue_grey4 mt-1">Console v2</p> */}
      </div>

          {/* <img
                      src={logo}
                      alt="Installation Systems"
                      className="w-full h-full object-contain"
                    /> */}
      </>
    )}
  </div>
);

/* ── Collapse toggle ── */
const CollapseToggle = ({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) => (
  <button
    onClick={onToggle}
    className="hidden lg:flex items-center justify-center w-full px-4 py-3 text-on-navy-tertiary hover:text-on-navy dark:hover:text-gray-300 hover:bg-white/15 dark:hover:bg-gray-800 border-t border-white/15 dark:border-gray-800 transition-colors"
    title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
  >
    <PanelLeft size={16} className={`transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`} />
  </button>
);

/* ── Sidebar content (shared by desktop rail + mobile drawer) ── */
const SidebarContent = ({
  collapsed,
  onToggleCollapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
}) => {
  // Server-authoritative role (same `/v1/users/me` query RequireAdminAccess
  // already resolved before this shell ever mounts — cached, no extra
  // network call). Never trust a cached/local copy for gating (§L).
  const { data } = useMe();
  const role = data?.user.role;
  const perms = {
    canManageUsers: role?.canManageUsers ?? false,
    canManageMasterData: role?.canManageMasterData ?? false,
  };
  const visibleNav = nav.filter((section) => isSectionVisible(section.section, perms));

  return (
    <div className={[
      "flex flex-col h-full bg-penn-900 dark:bg-gray-900 border-r border-white/15 dark:border-gray-800 transition-all duration-300 ease-in-out",
      collapsed ? "w-15" : "w-67",
    ].join(" ")}>
      <Logo collapsed={collapsed} />
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-1 px-2">
        {visibleNav.map((section) => (
          <div key={section.section}>
            <SectionLabel label={section.section} collapsed={collapsed} />
            {section.items.map((item) =>
              isGroup(item) ? (
                <GroupItem
                  key={item.label}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  onExpandSidebar={collapsed ? onToggleCollapsed : undefined}
                />
              ) : collapsed ? (
                <Tooltip key={item.to} label={item.label}>
                  <LeafLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
                </Tooltip>
              ) : (
                <LeafLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              )
            )}
          </div>
        ))}
      </nav>
      {onToggleCollapsed && <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />}
    </div>
  );
};

/* ── Main Sidebar ── */
export const Sidebar = () => {
  const { collapsed, mobileOpen, toggleCollapsed, closeMobile } = useSidebar();

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col shrink-0 h-full">
        <SidebarContent collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      {/* Mobile drawer overlay — always renders the expanded sidebar so the
          drawer view is consistent regardless of the desktop collapse state,
          and closes itself when a nav link is tapped. */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeMobile}
          />
          <aside className="relative z-50 flex flex-col h-full shadow-2xl">
            <SidebarContent collapsed={false} onNavigate={closeMobile} />
          </aside>
        </div>
      )}
    </>
  );
};
